"use client";
import { useState } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import { ShallowLink } from "../Link";

export default function TabBox({
  shallow,
  override,
  tabs = [],
  initial = "0",
  variant,
  noOutline,
  onChange,
  overflow = "hidden",
  borderRadius = "var(--shape-lg-top)",
  sx
}) {
  const [tab, setTab] = useState(initial);
  const activeIndex = override ? parseInt(override) : parseInt(tab);
  const activeLabel = tabs[activeIndex]?.label;
  const { Component } = tabs[activeIndex] ?? {};

  return (
    <Box
      sx={{
        overflow,
        border: noOutline ? undefined : 1,
        borderRadius: noOutline ? undefined : "var(--shape-sm)",
        borderColor: noOutline ? undefined : "var(--outline-variant)"
      }}
    >
      <Tabs
        value={override || tab}
        scrollButtons="none"
        variant={variant || (noOutline ? "fullWidth" : variant)}
        onChange={(_, tab) => setTab(tab) || onChange?.(tab)}
        slotProps={{
          indicator: {
            // 1. We render the active label INSIDE the indicator
            children: (
              <span className="MuiTabs-indicatorSpan">{activeLabel}</span>
            ),
            sx: {
              display: "flex",
              justifyContent: "center",
              backgroundColor: "transparent", // Hide the sliding container
              height: "100%",
              // This ensures the indicator text doesn't wrap weirdly during transition
              whiteSpace: "nowrap"
            }
          }
        }}
        sx={{
          borderBottom: 1,
          borderBottomColor: "var(--outline-variant)",
          "& .MuiTabs-indicatorSpan": {
            // 2. Make the ghost text invisible, but keep its width
            color: "transparent",
            // 3. Style the "Underline" relative to this text width
            position: "relative",
            display: "flex",
            alignItems: "flex-end", // Push the border to the bottom
            height: "100%",
            "&::after": {
              content: '""',
              width: "100%", // Exactly matches the ghost text width
              height: "4px",
              bgcolor: "var(--primary-color)",
              borderRadius: "4px 4px 0 0",
              position: "absolute",
              bottom: 0
            }
          },
          ...sx
        }}
      >
        {tabs.map(({ label, Icon, iconPosition, href, onClick, sx }, index) => (
          <Tab
            key={index}
            label={label}
            value={index.toString()}
            icon={Icon ? <Icon /> : undefined}
            href={href}
            LinkComponent={shallow ? ShallowLink : undefined}
            onClick={onClick}
            iconPosition={iconPosition}
            sx={{
              color: "var(--secondary-color)",
              transition: "all 200ms var(--motion-easing-standard)",
              typography: { compact: "labelLg", expanded: "titleSm" },
              // Optional: Enforce min-width to ensure short labels have some click area
              minWidth: 60,
              "&:hover": {
                borderRadius,
                color: "var(--primary-color)",
                bgcolor: theme =>
                  theme.addAlpha("var(--surface-container-highest)", 0.3)
              },
              ...sx
            }}
          />
        ))}
      </Tabs>
      {Component ? <Component /> : null}
    </Box>
  );
}
